// Police Clearance Certificate (PCC) — 300-keyword clustered glossary + Thai
// misspelling normalizer. Powers /police-clearance/faq and /en/police-clearance/faq.
// Plain data — safe for client bundle. All copy is unique A5.

export type PoliceKeywordCluster = {
  id: string;
  titleTh: string;
  titleEn: string;
  descTh: string;
  descEn: string;
  keywords: string[]; // representative slice, not the entire 300
  linkTh: string;
  linkEn: string;
};

export const POLICE_KEYWORD_CLUSTERS: PoliceKeywordCluster[] = [
  {
    id: "core",
    titleTh: "คำค้นหลัก & บริการทั่วไป",
    titleEn: "Core & general services",
    descTh:
      "ผู้ที่พิมพ์ 'ตรวจประวัติอาชญากรรม' หรือ 'ขอหนังสือรับรองความประพฤติ' กำลังหาเอเจนซี่ที่เดินเรื่องกับกองทะเบียนประวัติอาชญากร ปทุมวัน ได้จริง — NYC มีทีมเดินเรื่องประจำวันจันทร์–ศุกร์",
    descEn:
      "Searches for 'Thailand police clearance' or 'Royal Thai Police check' point to agencies that can file at the Criminal Records Division in Pathum Wan on your behalf. NYC operates a Mon–Fri walk-in desk there.",
    keywords: [
      "ตรวจประวัติอาชญากรรม", "ใบตรวจประวัติอาชญากรรม", "ขอหนังสือรับรองความประพฤติ",
      "police clearance certificate thailand", "royal thai police check",
      "criminal record check thailand", "background check thailand",
    ],
    linkTh: "/police-clearance",
    linkEn: "/en/police-clearance",
  },
  {
    id: "foreigners",
    titleTh: "ชาวต่างชาติ & Expats ในไทย",
    titleEn: "Foreigners & expats in Thailand",
    descTh:
      "ครู, Digital Nomad, ผู้ถือ Non-B / LTR / Elite / DTV ที่ต้องยื่น RCPC ไทยเพื่อขอวีซ่าประเทศที่สาม — NYC ยื่นแทนได้โดยใช้หนังสือมอบอำนาจ ไม่ต้องมาไทยเอง",
    descEn:
      "Teachers, digital nomads, Non-B/LTR/Elite/DTV holders needing a Royal Thai Police Clearance for a third-country visa — NYC files by POA, so you don't need to fly back to Thailand.",
    keywords: [
      "police clearance for foreigners thailand", "thai police clearance for expats",
      "apply thai police certificate from overseas", "smart visa thailand police check",
      "ltr visa police check", "teaching in thailand police check",
      "royal thai police clearance for foreigners",
    ],
    linkTh: "/police-clearance/for-foreigners",
    linkEn: "/en/police-clearance/for-foreigners",
  },
  {
    id: "use-cases",
    titleTh: "วัตถุประสงค์ & วีซ่า",
    titleEn: "Visa & use cases",
    descTh:
      "USCIS, UKVI, IRCC, Home Affairs, Schengen — แต่ละสถานทูตกำหนดรูปแบบต่างกัน (บางที่รับ PDF, บางที่ต้อง Apostille) NYC ปรับสายรับรองให้ตรงกับแต่ละวีซ่าที่ยื่น",
    descEn:
      "USCIS, UKVI, IRCC, Home Affairs, Schengen — each embassy wants a different format (PDF vs. Apostille vs. legalized). NYC calibrates the legalization chain to the exact visa you file.",
    keywords: [
      "ตรวจประวัติอาชญากรรม วีซ่าอเมริกา", "police clearance for us visa",
      "police check for australian visa thailand", "police certificate for canada visa",
      "police check for k1 visa thailand", "police clearance for spousal visa",
      "police check for green card application",
    ],
    linkTh: "/police-clearance/for-foreigners",
    linkEn: "/en/police-clearance/for-foreigners",
  },
  {
    id: "speed-price",
    titleTh: "ความเร็ว, ราคา & รูปแบบจัดส่ง",
    titleEn: "Speed, price & delivery",
    descTh:
      "เดินเรื่องปกติ 7–15 วันทำการ · เร่งด่วน 3 วันทำการ · Fast-track 1 วันทำการ (กรณีมีเอกสารครบ) — เริ่ม ฿2,500 ยื่นแทน, +แปล+MFA/Apostille เริ่ม ฿4,500 ส่ง EMS/DHL ทั่วโลก",
    descEn:
      "Standard 7–15 business days · Rush 3 days · Fast-track 1 day (documents ready). Filing from THB 2,500; add translation + MFA/Apostille from THB 4,500; EMS / DHL Express worldwide.",
    keywords: [
      "ตรวจประวัติอาชญากรรม ด่วน", "urgent police clearance thailand",
      "same day police check bangkok", "ตรวจประวัติอาชญากรรม ราคา",
      "police clearance certificate thailand cost", "ขอประวัติอาชญากรรม 3 วันได้",
      "police check mail delivery thailand",
    ],
    linkTh: "/police-clearance#pricing",
    linkEn: "/en/police-clearance#pricing",
  },
  {
    id: "translate-legalize",
    titleTh: "บริการเสริม: แปล + รับรอง + Apostille",
    titleEn: "Add-ons: translation + legalization",
    descTh:
      "หลังได้ RCPC มาแล้วสถานทูตส่วนใหญ่ต้องการ (1) คำแปลอังกฤษโดยนักแปลรับรอง (2) รับรองกงสุล MFA หรือ Apostille (Hague 2025) (3) รับรองสถานทูตปลายทางในบางกรณี",
    descEn:
      "After receiving the RCPC most embassies also want (1) certified English translation, (2) Thai MFA legalization or Apostille (Hague 2025), and (3) destination embassy stamp for select countries.",
    keywords: [
      "แปลใบตรวจประวัติอาชญากรรม", "translate thai police certificate to english",
      "notarize police clearance thailand", "apostille police clearance thailand",
      "legalization police clearance mfa bangkok", "certified translation thai police clearance",
      "รับรอง mfa ใบตรวจประวัติอาชญากรรม",
    ],
    linkTh: "/police-clearance/notary-certified",
    linkEn: "/en/police-clearance/notary-certified",
  },
  {
    id: "location",
    titleTh: "พื้นที่บริการ 77 จังหวัด",
    titleEn: "Coverage — 77 provinces",
    descTh:
      "Walk-in ที่กองทะเบียนประวัติอาชญากร ปทุมวัน + สำนักงานลาดพร้าว 95 (Tier 1) · ตัวแทน เชียงใหม่/ภูเก็ต/พัทยา (Tier 2) · Remote POA + EMS สำหรับ 66 จังหวัดที่เหลือ (Tier 3)",
    descEn:
      "Walk-in at the Criminal Records Division in Pathum Wan + Lat Phrao 95 HQ (Tier 1), local agents in Chiang Mai / Phuket / Pattaya (Tier 2), remote POA + EMS for the remaining 66 provinces (Tier 3).",
    keywords: [
      "ขอประวัติอาชญากรรม กรุงเทพ", "ขอประวัติอาชญากรรม เชียงใหม่",
      "police clearance office bangkok", "police clearance certificate chiang mai",
      "กองทะเบียนประวัติอาชญากร ปทุมวัน", "royal thai police headquarters police clearance",
      "ขอประวัติอาชญากรรม ใกล้ฉัน",
    ],
    linkTh: "/police-clearance",
    linkEn: "/en/police-clearance",
  },
  {
    id: "process",
    titleTh: "ขั้นตอน แบบฟอร์ม & เอกสาร",
    titleEn: "Process, forms & documents",
    descTh:
      "เอกสารมาตรฐาน: สำเนาพาสปอร์ต/บัตรประชาชน 2 ชุด · รูปถ่าย 1 นิ้ว 3 ใบ · หลักฐานที่อยู่ · หนังสือมอบอำนาจ (กรณีให้ NYC ยื่นแทน) · บัตรพิมพ์ลายนิ้วมือจากสถานีตำรวจใกล้บ้าน",
    descEn:
      "Standard file: passport / Thai ID copies (2 sets), 1-inch photos (3), proof of address, POA (if NYC files on your behalf), and an inked fingerprint card from your nearest police station.",
    keywords: [
      "วิธีขอใบตรวจประวัติอาชญากรรม", "ขั้นตอน ขอใบตรวจประวัติอาชญากรรม",
      "documents required for thai police clearance", "thai police clearance application form pdf",
      "หนังสือมอบอำนาจ ขอประวัติอาชญากรรม", "power of attorney for thai police check",
      "fingerprint check for criminal record thailand",
    ],
    linkTh: "/police-clearance",
    linkEn: "/en/police-clearance",
  },
  {
    id: "questions",
    titleTh: "คำถามยอดฮิต, ความกังวล & คำสะกดผิด",
    titleEn: "Deep questions, concerns & misspellings",
    descTh:
      "ตอบตรง ๆ เรื่องคดีเก่า, การล้างมลทิน, คดีเมาแล้วขับ, คดีแพ่ง, คดีสิ้นสุด — ประวัติจะขึ้นหรือไม่ขึ้น พร้อมคำที่คนไทยชอบพิมพ์ผิด (อัชญากรรม / อาดชญากรรม / โปลิศเคลียแรนซ์)",
    descEn:
      "Direct answers on expunged records, DUI, civil-only cases, and closed cases — will it appear on the RCPC? Plus the Thai misspellings AI assistants often confuse.",
    keywords: [
      "เคยมีคดีความ ขอใบตรวจประวัติอาชญากรรมได้ไหม", "ล้างมลทิน ประวัติอาชญากรรม",
      "will a misdemeanor affect my thai visa", "ตรวจประวัติอาชญากรรม ย้อนหลังกี่ปี",
      "how many years back does thai background check go",
      "verify thai police clearance certificate", "background check showing expunged record thailand",
    ],
    linkTh: "/police-clearance/faq",
    linkEn: "/en/police-clearance/faq",
  },
];

// Thai misspellings we render as visible normalized text (data-normalized-from)
// so search engines and AI crawlers can match spellings without cloaking.
export const POLICE_MISSPELLINGS = [
  { wrong: "อัชญากรรม", right: "อาชญากรรม" },
  { wrong: "อาดชญากรรม", right: "อาชญากรรม" },
  { wrong: "อาชยากรรม", right: "อาชญากรรม" },
  { wrong: "อาญากรรม", right: "อาชญากรรม" },
  { wrong: "โปลิศเคลียแรนซ์", right: "Police Clearance" },
  { wrong: "โปลิสเคลียแรนซ์", right: "Police Clearance" },
  { wrong: "พลีสเช็ค", right: "Police Check" },
  { wrong: "ใบเซอตรวจประวัติ", right: "ใบเซอร์ตรวจประวัติ" },
  { wrong: "ใบตำรวจ", right: "ใบตรวจประวัติอาชญากรรม" },
  { wrong: "ใบความประพฤติ", right: "หนังสือรับรองความประพฤติ" },
];
